/**
 * Credit Reports Module - TanStack Query Hooks
 *
 * This module provides all hooks for credit report operations including:
 * - Get credit report details
 * - Start PF (Persona Física) credit report
 * - Start PM (Persona Moral) credit report
 * - Confirm PIN for credit report
 * - Resend PIN for credit report
 *
 * Note: Enable/Disable credit report operations are part of investigations module
 * (useInvestigations.ts) as they relate to investigation status management.
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  PFRequestData,
  PMRequestData,
} from "js/components/ExpressReport/types";

import {
  confirmPinCreditReport,
  getCreditReport,
  resendPinCreditReport,
  startPfCreditReport,
  startPmCreditReport,
} from "../api/creditReport";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get credit report by ID
 * @param reportId - The credit report ID
 * @param enabled - Whether the query should run (default: true)
 * @returns Credit report details with loading and error states
 */
export const useGetCreditReport = (
  reportId: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.creditReports.detail(reportId),
    queryFn: () => getCreditReport(reportId),
    enabled: !!reportId && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// ============================================================================
// MUTATION HOOKS (START, CONFIRM, RESEND)
// ============================================================================

/**
 * Start PF (Persona Física) credit report
 * Invalidates: credit report detail, investigations
 */
export const useStartPfCreditReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      reportData,
    }: {
      reportId: string;
      reportData: PFRequestData;
    }) => startPfCreditReport(reportId, reportData),
    onSuccess: (data, variables) => {
      toast.success("Reporte de crédito PF iniciado exitosamente");
      // Invalidate specific credit report
      queryClient.invalidateQueries({
        queryKey: queryKeys.creditReports.detail(variables.reportId),
      });
      // Invalidate investigations as credit report is part of investigation
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.lists(),
      });
      // Invalidate advisor credits as starting report may consume credits
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.credits(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al iniciar el reporte de crédito PF";
      toast.error(errorMessage);
    },
  });
};

/**
 * Start PM (Persona Moral) credit report
 * Invalidates: credit report detail, investigations
 */
export const useStartPmCreditReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      reportData,
    }: {
      reportId: string;
      reportData: PMRequestData;
    }) => startPmCreditReport(reportId, reportData),
    onSuccess: (data, variables) => {
      toast.success("Reporte de crédito PM iniciado exitosamente");
      // Invalidate specific credit report
      queryClient.invalidateQueries({
        queryKey: queryKeys.creditReports.detail(variables.reportId),
      });
      // Invalidate investigations as credit report is part of investigation
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.lists(),
      });
      // Invalidate advisor credits as starting report may consume credits
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.credits(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al iniciar el reporte de crédito PM";
      toast.error(errorMessage);
    },
  });
};

/**
 * Confirm PIN for credit report
 * Invalidates: credit report detail, investigations
 * Note: Toast notifications are handled by the component for specific error messages
 */
export const useConfirmPinCreditReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reportId,
      pin,
      confirmPin,
    }: {
      reportId: string;
      pin: string;
      confirmPin: string;
    }) => confirmPinCreditReport(reportId, pin, confirmPin),
    onSuccess: (data, variables) => {
      // Invalidate specific credit report
      queryClient.invalidateQueries({
        queryKey: queryKeys.creditReports.detail(variables.reportId),
      });
      // Invalidate investigations as credit report status changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.lists(),
      });
    },
  });
};

/**
 * Resend PIN for credit report
 * Does not invalidate queries as it's just a notification action
 * Note: Toast notifications are handled by the component for custom messages
 */
export const useResendPinCreditReport = () => {
  return useMutation({
    mutationFn: ({ reportId, method }: { reportId: string; method: string }) =>
      resendPinCreditReport(reportId, method),
  });
};
